import { ConnectionGraph, Edge } from "./connections";
import { TimerMode, ValueOperator, valueOperatorKind } from "./objects";
import { DELAY_QUEUE_CAP, LAMP_FLASH_DECAY_S } from "./constants";

export interface PulsePayload {
  count: number;
  impactSum: number;
}

export interface EvalResult {
  emitInByEmitter: Map<string, PulsePayload>;
  globalResetPulsed: boolean;
}

/**
 * Per-frame node census + clock sample the evaluator advances its runtime state
 * with. `nowSec` is the sim clock (clockTimeSec) and `cyclePhase` the
 * performance-cycle phase in [0,1) — one full loop of all placed sequencer
 * patterns, or one clock period when none is placed (computed by the caller) —
 * sampled once per frame so every timer reads the same instant. `timerModeById`
 * maps each placed timer to what its valueOut emits; `decaySById` maps each
 * placed decay node to its decay time in seconds (elapsed-start and level state
 * stay runtime-only in the evaluator). `delaySById` maps each placed delay node
 * to its delay duration in seconds. `widgetValueById` maps each placed button /
 * knob widget to the value its valueOut emits this frame (button: 1 while
 * held, else 0; knob: its rotary value); `lampIds` lists the placed lamps whose
 * flash levels the evaluator advances.
 */
export interface NodeInputs {
  countIds: readonly string[];
  randomIds: readonly string[];
  timerModeById: ReadonlyMap<string, TimerMode>;
  decaySById: ReadonlyMap<string, number>;
  delaySById: ReadonlyMap<string, number>;
  widgetValueById: ReadonlyMap<string, number>;
  lampIds: readonly string[];
  routerIds: readonly string[];
  routerHoldSById: ReadonlyMap<string, number>;
  routerHoldResetById: ReadonlyMap<string, boolean>;
  seqIds: readonly string[];
  seqValuesById: ReadonlyMap<string, readonly number[]>;
  curveCycleById: ReadonlyMap<string, number>;
  globalIds: readonly string[];
  nowSec: number;
  cyclePhase: number;
}

interface DelayEntry {
  fireAt: number;
  payload: PulsePayload;
}

/**
 * A value-producing node for the value resolver. A calc node carries its
 * operation plus the typed operand used for a binary op when operand2In is
 * unconnected. A count node (`isCount`) ignores both and resolves to the live
 * accumulator the evaluator holds. Built per frame from the placed nodes.
 */
export interface ValueNode {
  operator: ValueOperator;
  operand: number;
  isCount?: boolean;
}

/**
 * A router fans pulse to only one of its routeOut edges each frame, so its
 * output is keyed per edge (`<id>#<edgeId>`) instead of by the bare source id
 * every other pulse source uses. Reading the source pulse through this key keeps
 * every consumer fromPort-generic: a routeOut edge only sees the pulse on the
 * frame the cursor selected that edge.
 */
function pulseFrom(pulses: Map<string, PulsePayload>, e: Edge): PulsePayload | undefined {
  return pulses.get(e.fromPort === "routeOut" ? `${e.from}#${e.id}` : e.from);
}

function sumPulse(edges: readonly Edge[], pulseByObject: Map<string, PulsePayload>): number {
  let total = 0;
  for (const e of edges) {
    total += pulseFrom(pulseByObject, e)?.count ?? 0;
  }
  return total;
}

/**
 * Linearly interpolates a curve's sample array at a float position. `pos` past
 * the last index wraps via the modulo, so a periodic curve is continuous across
 * the i=len-1 → i=0 seam; an integer `pos` returns that sample exactly (frac 0).
 */
function lerpSample(values: readonly number[], pos: number): number {
  const len = values.length;
  const base = Math.floor(pos);
  const frac = pos - base;
  const i0 = ((base % len) + len) % len;
  const i1 = (i0 + 1) % len;
  return values[i0] * (1 - frac) + values[i1] * frac;
}

function addPulse(map: Map<string, PulsePayload>, id: string, p: PulsePayload): void {
  const existing = map.get(id);
  map.set(id, existing
    ? { count: existing.count + p.count, impactSum: existing.impactSum + p.impactSum }
    : { count: p.count, impactSum: p.impactSum });
}

/**
 * Applies a binary value op. Division and modulo pass the left operand through
 * unchanged when the divisor is 0 (no NaN); modulo is the positive remainder
 * (always in [0, |b|)).
 */
function applyBinary(operator: ValueOperator, a: number, b: number): number {
  if (operator === "add") {
    return a + b;
  }
  if (operator === "sub") {
    return a - b;
  }
  if (operator === "mul") {
    return a * b;
  }
  if (operator === "div") {
    return b === 0 ? a : a / b;
  }
  return b === 0 ? a : ((a % b) + b) % b;
}

/**
 * Applies a unary value op. `sqrt` clamps its input to ≥0 and `recip` passes 0
 * through unchanged, so neither yields NaN/Infinity; `sin` / `cos` read radians.
 */
function applyUnary(operator: ValueOperator, a: number): number {
  switch (operator) {
    case "floor": return Math.floor(a);
    case "frac": return a - Math.floor(a);
    case "round": return Math.round(a);
    case "ceil": return Math.ceil(a);
    case "abs": return Math.abs(a);
    case "neg": return -a;
    case "sign": return Math.sign(a);
    case "sqrt": return Math.sqrt(Math.max(a, 0));
    case "square": return a * a;
    case "recip": return a === 0 ? a : 1 / a;
    case "sin": return Math.sin(a);
    case "cos": return Math.cos(a);
    default: return a;
  }
}

/**
 * Deterministic pseudo-random hash of a value into [0,1): the same input always
 * yields the same output. Used by a random calc whose value-in (seed) is wired,
 * so a reproducible random can be keyed off a count / pitch / position.
 */
function hash01(x: number): number {
  const s = Math.sin(x * 127.1 + 311.7) * 43758.5453123;
  return s - Math.floor(s);
}

/**
 * Per-frame dataflow evaluator over the ConnectionGraph. Pulse propagation is a
 * single hop (only object pulseOut carries pulse; value nodes expose only their
 * value), so each `count` node reads its inc/reset in-edges directly from the
 * collider pulse map. The accumulated inc count is the only retained state and is
 * runtime-only (reset on reload — never persisted); a value node's output is then
 * computed from its op and its (recursively resolved) value inputs.
 */
export class SignalEvaluator {
  private readonly accumulators: Map<string, number>;
  private readonly randomValues: Map<string, number>;
  private readonly timerStarts: Map<string, number>;
  private readonly timerModes: Map<string, TimerMode>;
  private readonly decayLevels: Map<string, number>;
  private readonly delayQueues: Map<string, DelayEntry[]>;
  private readonly lampFlashes: Map<string, number>;
  private readonly routerCursors: Map<string, number>;
  private readonly routerDwellStart: Map<string, number>;
  private readonly routerLastPulse: Map<string, number>;
  private readonly seqCursors: Map<string, number>;
  private readonly orbitProgress: Map<string, number>;
  private widgetValues: ReadonlyMap<string, number>;
  private seqValuesById: ReadonlyMap<string, readonly number[]>;
  private curveCycleById: ReadonlyMap<string, number>;
  private frameNow: number;
  private framePhase: number;
  private frameDt: number;

  constructor() {
    this.accumulators = new Map<string, number>();
    this.randomValues = new Map<string, number>();
    this.timerStarts = new Map<string, number>();
    this.timerModes = new Map<string, TimerMode>();
    this.decayLevels = new Map<string, number>();
    this.delayQueues = new Map<string, DelayEntry[]>();
    this.lampFlashes = new Map<string, number>();
    this.routerCursors = new Map<string, number>();
    this.routerDwellStart = new Map<string, number>();
    this.routerLastPulse = new Map<string, number>();
    this.seqCursors = new Map<string, number>();
    this.orbitProgress = new Map<string, number>();
    this.widgetValues = new Map<string, number>();
    this.seqValuesById = new Map<string, readonly number[]>();
    this.curveCycleById = new Map<string, number>();
    this.frameNow = 0;
    this.framePhase = 0;
    this.frameDt = 0;
  }

  /**
   * Clears runtime-only graph state (queues, envelopes, counters, cursors).
   * Use when scene topology is restored externally (undo/redo) so stale
   * transient signal state cannot leak into the restored graph.
   */
  resetRuntimeState(): void {
    this.accumulators.clear();
    this.randomValues.clear();
    this.timerStarts.clear();
    this.timerModes.clear();
    this.decayLevels.clear();
    this.delayQueues.clear();
    this.lampFlashes.clear();
    this.routerCursors.clear();
    this.routerDwellStart.clear();
    this.routerLastPulse.clear();
    this.seqCursors.clear();
    this.orbitProgress.clear();
  }

  /**
   * Advances one frame. `pulseByObject` maps each collider object id to this
   * frame's contact count (its pulseOut). Each value node's accumulator is set to
   * 0 when any reset pulse arrives (reset wins over inc in the same frame), else
   * incremented by the summed inc pulses (only `count` nodes read this, but every
   * node accumulates harmlessly since the others have no inc edges). Each timer's
   * start instant is (re)anchored to `nowSec` on first sight or when it lies in
   * the future (the sim clock was rewound by a clock reset).
   * Each decay node's level snaps to 1 on any chargeIn pulse (capped at 1
   * regardless of the pulse count) and otherwise falls linearly by
   * dt / decayS toward 0; decayS <= 0 forces 0 (no division). dt is derived
   * from successive `nowSec` samples, clamped to >= 0 across a clock rewind.
   * Delay queues resolve before every pulse consumer: entries due this frame
   * merge into a frame-local pulse pool under the delay node's id, so a delayed
   * pulse drives inc / reset / chargeIn / emitIn — and another delay's delayIn —
   * exactly like a collider's pulseOut. A delayS <= 0 input bypasses the queue
   * and merges immediately (same-frame pass-through).
   * Each lamp's flash level snaps to 1 on any flashIn pulse and otherwise falls
   * linearly by dt / LAMP_FLASH_DECAY_S toward 0 (the decay-node envelope shape).
   * A clock rewind (nowSec < the last frame's) clears the count accumulators and
   * delay queues and resets every decay / lamp envelope to 0, so a reset replays
   * from silence and an empty count.
   * Returns the per-emitter summed external emit count for emitIn-driven
   * emitters, plus whether any global node received a reset pulse this frame
   * (the caller defers the actual clock reset to the frame boundary to avoid
   * re-entering evaluate). Nodes absent from `valueIds` / `timerModeById` /
   * `decaySById` / `delaySById` (deleted) are dropped so their runtime state
   * resets if re-created.
   */
  evaluate(graph: ConnectionGraph, pulseByObject: Map<string, PulsePayload>, inputs: NodeInputs): EvalResult {
    const dt = Math.max(0, inputs.nowSec - this.frameNow);
    const rewound = inputs.nowSec < this.frameNow;
    this.frameDt = dt;
    this.frameNow = inputs.nowSec;
    this.framePhase = inputs.cyclePhase;
    this.widgetValues = inputs.widgetValueById;
    this.seqValuesById = inputs.seqValuesById;
    this.curveCycleById = inputs.curveCycleById;
    if (rewound) {
      this.accumulators.clear();
      this.randomValues.clear();
      this.delayQueues.clear();
      this.decayLevels.clear();
      this.lampFlashes.clear();
    }
    for (const id of [...this.delayQueues.keys()]) {
      if (!inputs.delaySById.has(id)) {
        this.delayQueues.delete(id);
      }
    }
    const pulses = new Map<string, PulsePayload>(pulseByObject);
    for (const id of inputs.delaySById.keys()) {
      const queue = this.delayQueues.get(id) ?? [];
      const remaining: DelayEntry[] = [];
      for (const entry of queue) {
        if (entry.fireAt <= this.frameNow) {
          addPulse(pulses, id, entry.payload);
        } else {
          remaining.push(entry);
        }
      }
      this.delayQueues.set(id, remaining);
    }
    for (const [id, delayS] of inputs.delaySById) {
      const queue = this.delayQueues.get(id)!;
      for (const e of graph.edgesTo(id, "delayIn")) {
        const p = pulseFrom(pulses, e);
        if (!p || p.count <= 0) {
          continue;
        }
        if (delayS <= 0) {
          addPulse(pulses, id, p);
        } else if (queue.length < DELAY_QUEUE_CAP) {
          queue.push({ fireAt: this.frameNow + delayS, payload: { count: p.count, impactSum: p.impactSum } });
        }
      }
    }
    this.routeRouters(graph, pulses, inputs, rewound);
    this.advanceSequencers(graph, pulses, inputs, rewound);
    const live = new Set<string>(inputs.countIds);
    for (const id of [...this.accumulators.keys()]) {
      if (!live.has(id)) {
        this.accumulators.delete(id);
      }
    }
    for (const id of inputs.countIds) {
      const resetSum = sumPulse(graph.edgesTo(id, "reset"), pulses);
      const incSum = sumPulse(graph.edgesTo(id, "inc"), pulses);
      const cur = this.accumulators.get(id) ?? 0;
      this.accumulators.set(id, resetSum > 0 ? 0 : cur + incSum);
    }
    const liveRandom = new Set<string>(inputs.randomIds);
    for (const id of [...this.randomValues.keys()]) {
      if (!liveRandom.has(id)) {
        this.randomValues.delete(id);
      }
    }
    for (const id of inputs.randomIds) {
      if (sumPulse(graph.edgesTo(id, "inc"), pulses) > 0) {
        this.randomValues.set(id, Math.random());
      }
    }
    for (const id of [...this.timerStarts.keys()]) {
      if (!inputs.timerModeById.has(id)) {
        this.timerStarts.delete(id);
        this.timerModes.delete(id);
      }
    }
    for (const [id, mode] of inputs.timerModeById) {
      this.timerModes.set(id, mode);
      const start = this.timerStarts.get(id);
      if (start === undefined || start > inputs.nowSec) {
        this.timerStarts.set(id, inputs.nowSec);
      }
    }
    for (const id of [...this.decayLevels.keys()]) {
      if (!inputs.decaySById.has(id)) {
        this.decayLevels.delete(id);
      }
    }
    for (const [id, decayS] of inputs.decaySById) {
      if (decayS <= 0) {
        this.decayLevels.set(id, 0);
        continue;
      }
      const chargeSum = sumPulse(graph.edgesTo(id, "chargeIn"), pulses);
      if (chargeSum > 0) {
        this.decayLevels.set(id, 1);
        continue;
      }
      const cur = this.decayLevels.get(id) ?? 0;
      this.decayLevels.set(id, Math.max(0, cur - dt / decayS));
    }
    const liveLamps = new Set<string>(inputs.lampIds);
    for (const id of [...this.lampFlashes.keys()]) {
      if (!liveLamps.has(id)) {
        this.lampFlashes.delete(id);
      }
    }
    for (const id of inputs.lampIds) {
      const flashSum = sumPulse(graph.edgesTo(id, "flashIn"), pulses);
      if (flashSum > 0) {
        this.lampFlashes.set(id, 1);
        continue;
      }
      const cur = this.lampFlashes.get(id) ?? 0;
      this.lampFlashes.set(id, Math.max(0, cur - dt / LAMP_FLASH_DECAY_S));
    }
    const emitInByEmitter = new Map<string, PulsePayload>();
    for (const e of graph.all()) {
      if (e.toPort !== "emitIn") {
        continue;
      }
      const p = pulseFrom(pulses, e);
      if (!p) {
        continue;
      }
      addPulse(emitInByEmitter, e.to, p);
    }
    let globalResetPulsed = false;
    for (const id of inputs.globalIds) {
      if (sumPulse(graph.edgesTo(id, "reset"), pulses) > 0) {
        globalResetPulsed = true;
      }
    }
    return { emitInByEmitter, globalResetPulsed };
  }

  /**
   * A router's routeOut edges in connection order (the graph's stable edge
   * order). Each edge is one routing destination; the port grows by wiring, so
   * the destination count is just the edge count (no fixed cap).
   */
  private routeOutEdges(graph: ConnectionGraph, id: string): Edge[] {
    return graph.edgesFrom(id).filter((e: Edge): boolean => e.fromPort === "routeOut");
  }

  /**
   * Distributes each router's routeIn pulse to one routeOut edge. A pulse is
   * always forwarded whole to the current output (count > 1 never split, never
   * dropped); the runtime-only cursor only advances — moving to the next edge —
   * when a hold time has elapsed, so several pulses can dwell on one output. With
   * holdS <= 0 the cursor advances every pulse (the original per-pulse switch).
   * With holdS > 0 the switch is gated by the dwell anchor: resetOnPulse false
   * (first) anchors to the dwell start (a fixed window per output), true (last)
   * anchors to the last pulse (switch only after holdS of silence). The switch is
   * decided before the forward so a boundary pulse rides the new output. Cursors
   * and hold timestamps clear on a clock rewind and drop with deleted routers.
   */
  private routeRouters(graph: ConnectionGraph, pulses: Map<string, PulsePayload>, inputs: NodeInputs, rewound: boolean): void {
    if (rewound) {
      this.routerCursors.clear();
      this.routerDwellStart.clear();
      this.routerLastPulse.clear();
    }
    const live = new Set<string>(inputs.routerIds);
    for (const id of [...this.routerCursors.keys()]) {
      if (!live.has(id)) {
        this.routerCursors.delete(id);
        this.routerDwellStart.delete(id);
        this.routerLastPulse.delete(id);
      }
    }
    const now = this.frameNow;
    for (const id of inputs.routerIds) {
      let count = 0;
      let impactSum = 0;
      for (const e of graph.edgesTo(id, "routeIn")) {
        const p = pulseFrom(pulses, e);
        if (p) {
          count += p.count;
          impactSum += p.impactSum;
        }
      }
      if (count <= 0) {
        continue;
      }
      const edges = this.routeOutEdges(graph, id);
      if (edges.length === 0) {
        continue;
      }
      let cursor = this.routerCursors.get(id) ?? 0;
      const holdS = inputs.routerHoldSById.get(id) ?? 0;
      if (holdS <= 0) {
        this.routerCursors.set(id, cursor + 1);
      } else {
        const dwellStart = this.routerDwellStart.get(id);
        if (dwellStart === undefined) {
          this.routerDwellStart.set(id, now);
        } else {
          const anchor = inputs.routerHoldResetById.get(id) === true
            ? this.routerLastPulse.get(id) ?? dwellStart
            : dwellStart;
          if (now - anchor >= holdS) {
            cursor += 1;
            this.routerCursors.set(id, cursor);
            this.routerDwellStart.set(id, now);
          }
        }
        this.routerLastPulse.set(id, now);
      }
      const target = edges[cursor % edges.length];
      addPulse(pulses, `${id}#${target.id}`, { count, impactSum });
    }
  }

  /**
   * The 1-based index (in connection order) of the routeOut edge the router's
   * next pulse will route to, or 0 when nothing is wired. For the node label;
   * reads the cursor the last evaluate advanced without mutating it.
   */
  routerNextOut(graph: ConnectionGraph, id: string): number {
    const edges = this.routeOutEdges(graph, id);
    if (edges.length === 0) {
      return 0;
    }
    const cursor = this.routerCursors.get(id) ?? 0;
    return (cursor % edges.length) + 1;
  }

  /**
   * Advances each value sequencer's (array / curve) volatile cursor one cell per
   * stepIn pulse-arrival frame. A count-0 stepIn (no contact this frame) never
   * advances (the router count:0 gate); the cursor is a monotonic counter read
   * modulo the live length, so a seqLen change re-normalizes without special
   * handling. Cursors clear on a clock rewind and drop with deleted nodes; the
   * cell value is read straight from the per-frame seqValues snapshot in
   * resolveValue, so there is no retained value state.
   */
  private advanceSequencers(graph: ConnectionGraph, pulses: Map<string, PulsePayload>, inputs: NodeInputs, rewound: boolean): void {
    if (rewound) {
      this.seqCursors.clear();
    }
    const live = new Set<string>(inputs.seqIds);
    for (const id of [...this.seqCursors.keys()]) {
      if (!live.has(id)) {
        this.seqCursors.delete(id);
      }
    }
    for (const id of inputs.seqIds) {
      const len = inputs.seqValuesById.get(id)?.length ?? 0;
      if (len <= 0) {
        continue;
      }
      if (sumPulse(graph.edgesTo(id, "stepIn"), pulses) <= 0) {
        continue;
      }
      this.seqCursors.set(id, (this.seqCursors.get(id) ?? 0) + 1);
    }
  }

  /** The current cursor column of a value sequencer, the counter taken modulo its live length. */
  seqCursor(id: string, len: number): number {
    if (len <= 0) {
      return 0;
    }
    return (this.seqCursors.get(id) ?? 0) % len;
  }

  /**
   * The cell index a value sequencer outputs this frame. With a value-in (`x`)
   * edge wired the node switches to evaluate mode: the [0,1]-clamped x picks a
   * cell at the seqLen resolution (`floor(x·len)`), so the sequence acts as a
   * lookup table / transfer function. With no x wired it is the volatile stepIn
   * cursor. A self-referential x is broken by the visiting guard (resolves 0).
   */
  private seqIndex(graph: ConnectionGraph, id: string, len: number, nodes: Map<string, ValueNode>, visiting: Set<string>): number {
    const xEdges = graph.edgesTo(id, "operand1In");
    if (xEdges.length > 0 && !visiting.has(id)) {
      visiting.add(id);
      const x = this.resolveValue(graph, xEdges[0].from, nodes, visiting);
      visiting.delete(id);
      return Math.min(len - 1, Math.max(0, Math.floor(Math.min(1, Math.max(0, x)) * len)));
    }
    return (this.seqCursors.get(id) ?? 0) % len;
  }

  /**
   * The continuous sample position a curve reads this frame. With an `operand1In`
   * (x) edge wired the node is a transfer function: the [0,1]-clamped x maps to
   * `x·(len-1)`. Otherwise the stepIn pulse count drives the phase: it advances
   * `1/cycleLength` per pulse and `cycleLength` pulses complete one pass, so
   * `pos = (count mod cycleLength)/cycleLength · len` (a wrapping lerp by the
   * sampler closes the loop between the last and first sample). A self-referential
   * x is broken by the visiting guard (resolves 0).
   */
  private curvePos(graph: ConnectionGraph, id: string, len: number, cycleLength: number, nodes: Map<string, ValueNode>, visiting: Set<string>): number {
    const xEdges = graph.edgesTo(id, "operand1In");
    if (xEdges.length > 0 && !visiting.has(id)) {
      visiting.add(id);
      const x = this.resolveValue(graph, xEdges[0].from, nodes, visiting);
      visiting.delete(id);
      return Math.min(1, Math.max(0, x)) * (len - 1);
    }
    const cycle = Math.max(1, cycleLength);
    return (((this.seqCursors.get(id) ?? 0) % cycle) / cycle) * len;
  }

  /** The cell index a sequencer displays / outputs this frame (evaluate-x or cursor), for the highlight. */
  seqDisplayIndex(graph: ConnectionGraph, id: string, nodes: Map<string, ValueNode>, len: number): number {
    if (len <= 0) {
      return 0;
    }
    const cycle = this.curveCycleById.get(id);
    if (cycle !== undefined) {
      return Math.min(len - 1, Math.floor(this.curvePos(graph, id, len, cycle, nodes, new Set<string>())));
    }
    return this.seqIndex(graph, id, len, nodes, new Set<string>());
  }

  /**
   * Resolves one entity's value output, dispatching on the source kind: a timer
   * returns its mode's value — elapsed seconds (`time`) or the performance-cycle
   * phase (`loop`), both sampled by the last evaluate — a decay node returns its
   * current level, a button / knob widget its frame value (held 1/0 / rotary
   * position), any other id falls through to the value node resolver.
   */
  private resolveValue(graph: ConnectionGraph, id: string, nodes: Map<string, ValueNode>, visiting: Set<string>): number {
    const timerStart = this.timerStarts.get(id);
    if (timerStart !== undefined) {
      return this.timerModes.get(id) === "loop" ? this.framePhase : this.frameNow - timerStart;
    }
    const decayLevel = this.decayLevels.get(id);
    if (decayLevel !== undefined) {
      return decayLevel;
    }
    const widgetValue = this.widgetValues.get(id);
    if (widgetValue !== undefined) {
      return widgetValue;
    }
    const seqValues = this.seqValuesById.get(id);
    if (seqValues !== undefined) {
      const len = seqValues.length;
      if (len === 0) {
        return 0;
      }
      const cycle = this.curveCycleById.get(id);
      if (cycle !== undefined) {
        return lerpSample(seqValues, this.curvePos(graph, id, len, cycle, nodes, visiting));
      }
      return seqValues[this.seqIndex(graph, id, len, nodes, visiting)];
    }
    return this.resolveValueOut(graph, id, nodes, visiting);
  }

  /**
   * The value a value node emits this frame. A `count` node returns its
   * accumulated inc total. A `random` op with a wired value-in (seed) returns a
   * deterministic hash of the seed into [0,1) (same seed → same output,
   * overriding the pulse path); with no seed wired it returns the held value the
   * last pulse generated (0 before the first pulse). A binary op returns `operand1 OP operand2`, where
   * operand1 is the resolved operand1In (0 when unconnected) and operand2 is the
   * resolved operand2In (the typed operand when unconnected). A unary op returns
   * `OP(operand1)`. Inputs resolve recursively via resolveValue (an upstream pin
   * may be a timer or decay output); a visited guard returns 0 on a hand-crafted
   * cycle.
   */
  private resolveValueOut(graph: ConnectionGraph, id: string, nodes: Map<string, ValueNode>, visiting: Set<string>): number {
    const node = nodes.get(id);
    if (!node || visiting.has(id)) {
      return 0;
    }
    if (node.isCount) {
      return this.accumulators.get(id) ?? 0;
    }
    const kind = valueOperatorKind(node.operator);
    if (kind === "random") {
      const seedEdges = graph.edgesTo(id, "operand1In");
      if (seedEdges.length > 0) {
        visiting.add(id);
        const seed = this.resolveValue(graph, seedEdges[0].from, nodes, visiting);
        visiting.delete(id);
        return hash01(seed);
      }
      return this.randomValues.get(id) ?? 0;
    }
    visiting.add(id);
    const operand1Edges = graph.edgesTo(id, "operand1In");
    const operand1 = operand1Edges.length > 0 ? this.resolveValue(graph, operand1Edges[0].from, nodes, visiting) : 0;
    let result: number;
    if (kind === "binary") {
      const operand2Edges = graph.edgesTo(id, "operand2In");
      const operand2 = operand2Edges.length > 0 ? this.resolveValue(graph, operand2Edges[0].from, nodes, visiting) : node.operand;
      result = applyBinary(node.operator, operand1, operand2);
    } else {
      result = applyUnary(node.operator, operand1);
    }
    visiting.delete(id);
    return result;
  }

  /**
   * The float value an entity emits on its valueOut — read by the Param-in /
   * gate drives and the CSS2D node labels. 0 when the id resolves to nothing.
   */
  valueOut(graph: ConnectionGraph, id: string, nodes: Map<string, ValueNode>): number {
    return this.resolveValue(graph, id, nodes, new Set<string>());
  }

  /**
   * The value a display node shows this frame: the resolved value of its wired
   * `valueIn` source, or 0 when nothing is connected. A display has no output of
   * its own, so this reads the upstream source directly rather than an accumulator.
   */
  displayValue(graph: ConnectionGraph, id: string, nodes: Map<string, ValueNode>): number {
    const edges = graph.edgesTo(id, "valueIn");
    return edges.length > 0 ? this.resolveValue(graph, edges[0].from, nodes, new Set<string>()) : 0;
  }

  /** A lamp's flash envelope level in [0,1], advanced by the last evaluate. */
  lampFlash(id: string): number {
    return this.lampFlashes.get(id) ?? 0;
  }

  /**
   * Rate-limited follow of an orbit's target-pose progress toward `target`
   * (clamped to [0,1]) so a full 0→1 glide takes `transitionS` seconds, stepped
   * by the last evaluate's dt — a value jump glides instead of snapping. A
   * transitionS <= 0 snaps immediately (no division). The first sight of an id
   * starts from 0 (base pose). Only target-wired orbits are advanced; the stored
   * value is the live progress transformGraph reads as the mode flag (present ⇒
   * target mode).
   */
  advanceOrbitProgress(id: string, target: number, transitionS: number): void {
    const goal = Math.min(1, Math.max(0, target));
    const cur = this.orbitProgress.get(id) ?? 0;
    const step = transitionS > 0 ? this.frameDt / transitionS : 1;
    const delta = goal - cur;
    const next = Math.abs(delta) <= step ? goal : cur + Math.sign(delta) * step;
    this.orbitProgress.set(id, next);
  }

  /**
   * The orbit's target-pose progress in [0,1], or undefined when the orbit is
   * not target-wired (continuous mode). transformGraph branches on presence.
   */
  orbitProgressOf(id: string): number | undefined {
    return this.orbitProgress.get(id);
  }

  /** Drops progress for orbits no longer target-wired, so they revert to continuous mode. */
  pruneOrbitProgress(live: ReadonlySet<string>): void {
    for (const id of [...this.orbitProgress.keys()]) {
      if (!live.has(id)) {
        this.orbitProgress.delete(id);
      }
    }
  }
}
